var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import * as UnlinkedApplicationForm from "../../../components/UnlinkedApplicationForm";
import { Button, NumberFormatField } from "@savvyinsure/react-ui";
import { completedPage, specifyMonthlyPolicyPremium } from "../../../lib/Avo";
import { savePremium, skippedAutoPremium, } from "../../../features/unlinked-application/unlinkedApplicationSlice";
import AppBody from "../../../components/AppBody";
import { EXPERIENCE } from "../../../constants";
import Header from "../../../components/Header";
import { NOT_INSURED_ISSUER_SLUG } from "../../../lib/constants";
import { PHASE } from "../../../routeConfig";
import { PivotReason } from "../../../features/applicant/applicantSlice";
import { TermOptions } from "../../../features/unlinked-application/constants";
import { calculatePremiumAmount } from "../../../utils/calculate";
import { convertDollarsToCents } from "../../../lib/util";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
var INSURED_HEADER_CONTENT = "If you stay with your current auto insurance, how much will it cost every";
var UNINSURED_HEADER_CONTENT = "How much do you expect to pay for auto insurance every";
var DEFAULT_SUB_HEADER_CONTENT = "If you don't know the exact amount, an estimate is okay!";
export function useAutoPremiumNextRoute() {
    return useAppSelector(function (state) {
        var isPrefill = state.configuration.experience === EXPERIENCE.PREFILL;
        var isBundleAutoFromHome = state.applicant.bundlePivotReason === PivotReason.BundleAutoFromHome;
        if (isPrefill) {
            return PHASE.UNLINKED_APPLICATION_AUTO_MILITARY;
        }
        if (isBundleAutoFromHome) {
            return PHASE.UNLINKED_APPLICATION_HOMEOWNERS_POLICY_HOLDER;
        }
        return PHASE.UNLINKED_APPLICATION_AUTO_POLICY_HOLDER_ADDRESS;
    });
}
export default function UnlinkedApplicationAutoPremium() {
    var _a;
    var commonAnalyticsProperties = useAppSelector(function (state) { return state.analytics.commonAnalyticsProperties; });
    var _b = useAppSelector(function (state) { return state.unlinkedApplication; }), entryAction = _b.entryAction, auto = _b.auto;
    var selectedIssuer = useAppSelector(function (state) { return state.applicant; }).currentIssuer.auto;
    var nextRoute = useAutoPremiumNextRoute();
    var push = useHistory().push;
    var dispatch = useAppDispatch();
    var _c = useForm({
        defaultValues: {
            premium: auto.premium,
            term: auto.term || TermOptions.Monthly,
        },
        mode: "all",
    }), register = _c.register, _d = _c.formState, errors = _d.errors, isValid = _d.isValid, handleSubmit = _c.handleSubmit, setFocus = _c.setFocus, watch = _c.watch;
    var isInsured = (selectedIssuer === null || selectedIssuer === void 0 ? void 0 : selectedIssuer.id) !== NOT_INSURED_ISSUER_SLUG;
    var _e = watch(["premium", "term"]), premiumValue = _e[0], termValue = _e[1];
    var paymentPeriod = termValue === TermOptions.Monthly
        ? "month"
        : termValue === TermOptions.TwelveMonths
            ? "year"
            : "6 months";
    var headerV3 = entryAction === "not-insured"
        ? UNINSURED_HEADER_CONTENT
        : INSURED_HEADER_CONTENT;
    var subHeader = isInsured
        ? DEFAULT_SUB_HEADER_CONTENT
        : "We won't show you quotes outside of your budget.";
    var monthlyPremiumAmount = register("premium", {
        required: true,
        setValueAs: function (value) {
            if (typeof value === "string") {
                return Number((value !== null && value !== void 0 ? value : "").replace(/,/g, ""));
            }
            return value;
        },
        validate: {
            isPositive: function (val) {
                return (val && val > 0) || "Please enter a positive premium amount";
            },
            validPremiumAmount: function (val) {
                var isMonthly = termValue === TermOptions.Monthly;
                var max = isMonthly ? 1000 : 100000;
                var message = isMonthly
                    ? "Please enter $999 if you pay more than that each month."
                    : "Please enter $99,999 if you pay more than that every ".concat(paymentPeriod, ".");
                return (val && val < max) || message;
            },
        },
    });
    function sendAnalytics(premiumAmount) {
        var hasPremium = typeof premiumAmount === "number";
        var monthlyPremiumAmount = hasPremium
            ? convertDollarsToCents(premiumAmount)
            : undefined;
        var selectedOption = !hasPremium
            ? "I don't know"
            : "".concat(monthlyPremiumAmount);
        completedPage(__assign(__assign({}, commonAnalyticsProperties), { selectedOption: selectedOption, optionSelected: selectedOption }));
        specifyMonthlyPolicyPremium(__assign(__assign({}, commonAnalyticsProperties), { monthlyPremiumAmount: monthlyPremiumAmount, isInsured: isInsured, clickedIDontKnow: !hasPremium }));
    }
    var handleSubmitForm = handleSubmit(function (formData, event) {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        var premium = formData.premium, term = formData.term;
        if (!premium || !term)
            return;
        var premiumNumber = +premium;
        var premiumAmount = calculatePremiumAmount(premiumNumber, +term);
        sendAnalytics(premiumAmount);
        dispatch(savePremium({ policyType: "auto", premium: premiumNumber, term: term }));
        push(isInsured ? PHASE.UNLINKED_APPLICATION_AUTO_POLICY_EXPIRATION : nextRoute);
    });
    React.useEffect(function () {
        setTimeout(function () { return setFocus("premium"); }, 200);
    }, [setFocus]);
    function handleOnCloseHeader() {
        if (!premiumValue || !termValue)
            return;
        dispatch(savePremium({
            policyType: "auto",
            premium: premiumValue,
            term: termValue,
        }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { onClose: handleOnCloseHeader, showDropShadow: true },
            React.createElement(UnlinkedApplicationForm.Progress, null)),
        React.createElement(AppBody, null,
            React.createElement(UnlinkedApplicationForm.ScrollingContainer, null,
                React.createElement("div", { className: "mb-4 flex-none pt-4" },
                    React.createElement(UnlinkedApplicationForm.Header, { className: "mb-1" }, "".concat(headerV3, " ").concat(paymentPeriod, "?")),
                    subHeader),
                React.createElement("form", { className: "mb-4 space-y-4", action: "#", onSubmit: handleSubmitForm },
                    React.createElement("div", { className: "relative" },
                        React.createElement("span", { className: "pointer-events-none absolute right-4 top-4 z-10 leading-none" },
                            "/",
                            paymentPeriod),
                        React.createElement(NumberFormatField, __assign({ id: "monthlyPremiumAmount", label: "Amount", "aria-invalid": Boolean(errors === null || errors === void 0 ? void 0 : errors.premium), defaultValue: auto.premium || undefined, errorMessage: (_a = errors.premium) === null || _a === void 0 ? void 0 : _a.message, inputPrefix: "$", thousandSeparator: "," }, monthlyPremiumAmount))),
                    React.createElement("div", { className: "space-y-4" },
                        React.createElement(Button, { id: "continue", disabled: !isValid }, "Continue"),
                        React.createElement(Button, { variant: "text", type: "button", onClick: function () {
                                sendAnalytics();
                                dispatch(skippedAutoPremium());
                                push(nextRoute);
                            }, className: "mx-auto" }, "I don't know")))))));
}
